import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { Withdrawal, CreateWithdrawalDto, StripeConnectStatus, AdminBalance } from "../types/payment";
import { apiService } from "../services/paymentAPI";

// Global flag to prevent multiple initializations across component re-mounts
let globalInitializationDone = false;

interface WithdrawalContextType {
  withdrawals: Withdrawal[];
  loading: boolean;
  error: string | null;
  stripeConnectStatus: StripeConnectStatus | null;
  adminBalance: AdminBalance | null;
  statusLoading: boolean;
  createWithdrawal: (withdrawal: CreateWithdrawalDto) => Promise<void>;
  cancelWithdrawal: (id: string) => Promise<void>;
  deleteWithdrawal: (id: string) => Promise<void>;
  refreshWithdrawals: () => Promise<void>;
  checkStripeConnectStatus: () => Promise<void>;
  checkAdminBalance: () => Promise<void>;
  refreshStripeConnectStatus: () => Promise<void>;
}

const WithdrawalContext = createContext<WithdrawalContextType | undefined>(undefined);

export const useWithdrawals = () => {
  const context = useContext(WithdrawalContext);
  if (!context) {
    throw new Error("useWithdrawals must be used within WithdrawalProvider");
  }
  return context;
};

export const WithdrawalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeConnectStatus, setStripeConnectStatus] = useState<StripeConnectStatus | null>(null);
  const [adminBalance, setAdminBalance] = useState<AdminBalance | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const hasCheckedStatus = useRef(false);
  const isInitialized = useRef(false);

  const refreshWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMyWithdrawals();
      
      // Handle response format
      const data = response.data || response;
      const withdrawalsList = Array.isArray(data) ? data : [];
      
      setWithdrawals(withdrawalsList);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  };

  const createWithdrawal = async (withdrawalData: CreateWithdrawalDto) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.createWithdrawal(withdrawalData);
      
      // Handle response format
      const newWithdrawal = response.data || response;
      
      setWithdrawals(prev => [newWithdrawal, ...prev]);
    } catch (err: any) {
      setError(err?.message || "Failed to create withdrawal");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelWithdrawal = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await apiService.cancelWithdrawal(id);
      
      // Update local state
      setWithdrawals(prev => 
        prev.map(withdrawal => 
          withdrawal.id === id 
            ? { ...withdrawal, status: 5 } // CANCELLED
            : withdrawal
        )
      );
    } catch (err: any) {
      setError(err?.message || "Failed to cancel withdrawal");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteWithdrawal = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteWithdrawal(id);
      
      // Remove from local state
      setWithdrawals(prev => prev.filter(withdrawal => withdrawal.id !== id));
    } catch (err: any) {
      setError(err?.message || "Failed to delete withdrawal");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const checkStripeConnectStatus = useCallback(async () => {
    if (hasCheckedStatus.current || statusLoading) {
      console.log("Stripe Connect status check skipped - already checked or loading");
      return; // Prevent multiple calls
    }
    
    console.log("Checking Stripe Connect status...");
    try {
      setStatusLoading(true);
      setError(null);
      const response = await apiService.checkStripeConnectStatus();
      setStripeConnectStatus(response.data || response);
      hasCheckedStatus.current = true; // Mark as checked
      console.log("Stripe Connect status checked successfully");
    } catch (err: any) {
      setError(err?.message || "Failed to check Stripe Connect status");
      console.error("Stripe Connect status check failed:", err);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const checkAdminBalance = useCallback(async () => {
    if (statusLoading) return; // Prevent multiple simultaneous calls
    
    try {
      setStatusLoading(true);
      setError(null);
      const response = await apiService.checkAdminBalance();
      setAdminBalance(response.data || response);
    } catch (err: any) {
      setError(err?.message || "Failed to check admin balance");
      console.error("Admin balance check failed:", err);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const refreshStripeConnectStatus = useCallback(async () => {
    hasCheckedStatus.current = false; // Reset the local flag
    globalInitializationDone = false; // Reset the global flag
    await checkStripeConnectStatus();
  }, [checkStripeConnectStatus]);

  // Load withdrawals on mount - Stripe Connect status will be checked on demand
  useEffect(() => {
    if (globalInitializationDone) {
      console.log("WithdrawalContext: Global initialization already done, skipping...");
      return;
    }

    const initializeData = async () => {
      console.log("WithdrawalContext: Starting global initialization...");
      globalInitializationDone = true;
      await refreshWithdrawals();
      // Don't automatically check Stripe Connect status - only on demand
      console.log("WithdrawalContext: Global initialization completed");
    };
    
    initializeData();
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <WithdrawalContext.Provider value={{
      withdrawals,
      loading,
      error,
      stripeConnectStatus,
      adminBalance,
      statusLoading,
      createWithdrawal,
      cancelWithdrawal,
      deleteWithdrawal,
      refreshWithdrawals,
      checkStripeConnectStatus,
      checkAdminBalance,
      refreshStripeConnectStatus,
    }}>
      {children}
    </WithdrawalContext.Provider>
  );
};
